import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { AdminFormat } from '@gg/shared';
import { Badge, buttonClasses, Table, Td, Th } from '@/components/ui';
import { formatStatusTone } from '@/lib/adminTones';
import { formatDateTime } from '@/lib/dates';
import { formatPrice } from '@/lib/i18n';
import { useAdminFormats } from '@/lib/queries';

/** A-01: every format of the event with places, the tier on sale now and its status. */
export function AdminFormats() {
  const { t } = useTranslation();
  const list = useAdminFormats();

  return (
    <section className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <h1 className="font-display text-section-lg font-bold">{t('admin.formats.title')}</h1>
        <Link to="/admin/formats/new" className={`${buttonClasses('primary', 'md')} w-auto!`}>
          {t('admin.formats.new')}
        </Link>
      </div>

      {list.isError && (
        <p role="alert" className="text-sm text-danger">
          {t('admin.formats.error')}
        </p>
      )}
      {list.data &&
        (list.data.formats.length === 0 ? (
          <p className="py-8 text-center text-ink-muted">{t('admin.formats.empty')}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('admin.formats.col.name')}</Th>
                <Th>{t('admin.formats.col.date')}</Th>
                <Th>{t('admin.formats.col.places')}</Th>
                <Th>{t('admin.formats.col.tier')}</Th>
                <Th>{t('admin.formats.col.status')}</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {list.data.formats.map((f) => (
                <FormatRow key={f.id} f={f} />
              ))}
            </tbody>
          </Table>
        ))}
    </section>
  );
}

function FormatRow({ f }: { f: AdminFormat }) {
  const { t } = useTranslation();
  const live = f.tiers.find((x) => x.live);
  const taken = f.paid + f.held;
  const full = f.capacity !== null && taken >= f.capacity;
  return (
    <tr>
      <Td>
        <Link to={`/admin/formats/${f.id}`} className="font-semibold">
          {f.name}
        </Link>
        {f.nameEn && <span className="block text-helper text-ink-muted">{f.nameEn}</span>}
      </Td>
      <Td className="whitespace-nowrap text-ink-muted">
        {f.startsAt ? formatDateTime(f.startsAt) : t('admin.none')}
      </Td>
      <Td className="whitespace-nowrap">
        {f.capacity === null
          ? t('admin.formats.placesUnlimited', { taken })
          : t('admin.formats.places', { taken, capacity: f.capacity })}
        {full && (
          <span className="ml-2">
            <Badge tone="danger">{t('admin.formats.full')}</Badge>
          </span>
        )}
      </Td>
      <Td>
        {live ? (
          <>
            {live.name} · <span className="font-semibold">{formatPrice(live.priceCents)}</span>
          </>
        ) : (
          <span className="text-ink-subtle">{t('admin.formats.notOnSale')}</span>
        )}
      </Td>
      <Td>
        <Badge tone={formatStatusTone(f.status)}>{t(`admin.formatStatus.${f.status}`)}</Badge>
      </Td>
      <Td className="text-right">
        <Link to={`/admin/formats/${f.id}`}>{t('admin.formats.edit')}</Link>
      </Td>
    </tr>
  );
}
